/**
 * Infinity Configurator — Save/Restore Design Code
 * Ported from Tonfly TFX configCode.ts pattern.
 * Encodes configurator state to a compressed URL-safe string, and decodes it back.
 */
(function () {
  'use strict';

  const PREFIX_V1 = 'INF1.';
  const PREFIX_V2 = 'INF2.';
  const BYTE_CHUNK_SIZE = 0x8000;
  const TEXT_FIELD_CAP = 300;
  const WORKER_ENDPOINT = 'https://tfx-photo-upload.young-sky-a2c1.workers.dev';

  const textEncoder = new TextEncoder();
  const textDecoder = new TextDecoder();

  function bytesToBinary(bytes) {
    let binary = '';
    for (let i = 0; i < bytes.length; i += BYTE_CHUNK_SIZE) {
      binary += String.fromCharCode(...bytes.subarray(i, i + BYTE_CHUNK_SIZE));
    }
    return binary;
  }

  function binaryToBytes(value) {
    const bytes = new Uint8Array(value.length);
    for (let i = 0; i < value.length; i++) {
      bytes[i] = value.charCodeAt(i);
    }
    return bytes;
  }

  function base64UrlEncodeBytes(bytes) {
    return btoa(bytesToBinary(bytes))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  function base64UrlDecodeToBytes(value) {
    const padded = value.padEnd(value.length + ((4 - (value.length % 4)) % 4), '=');
    const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
    return binaryToBytes(atob(base64));
  }

  function base64UrlEncode(value) {
    return base64UrlEncodeBytes(textEncoder.encode(value));
  }

  function base64UrlDecode(value) {
    return textDecoder.decode(base64UrlDecodeToBytes(value));
  }

  async function deflate(data) {
    const stream = new CompressionStream('deflate-raw');
    const writer = stream.writable.getWriter();
    writer.write(data);
    writer.close();
    const reader = stream.readable.getReader();
    const chunks = [];
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    const total = chunks.reduce((sum, c) => sum + c.length, 0);
    const result = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }
    return result;
  }

  async function inflate(data) {
    const stream = new DecompressionStream('deflate-raw');
    const writer = stream.writable.getWriter();
    writer.write(data);
    writer.close();
    const reader = stream.readable.getReader();
    const chunks = [];
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    const total = chunks.reduce((sum, c) => sum + c.length, 0);
    const result = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }
    return result;
  }

  let compressionSupported;
  function canCompress() {
    if (compressionSupported !== undefined) return compressionSupported;
    try {
      compressionSupported =
        typeof window !== 'undefined' &&
        'CompressionStream' in window &&
        'DecompressionStream' in window;
    } catch {
      compressionSupported = false;
    }
    return compressionSupported;
  }

  function capText(val) {
    if (typeof val !== 'string') return val;
    return val.length > TEXT_FIELD_CAP ? val.slice(0, TEXT_FIELD_CAP) : val;
  }

  /**
   * Build a minimal saveable state from the Alpine component's data.
   */
  function buildSavePayload(config, options, toggles, canopies, embroideryConfig) {
    const c = { ...config };
    c.design_instructions = capText(c.design_instructions);
    c.special_instructions = capText(c.special_instructions);
    c.previous_serial = capText(c.previous_serial);

    const payload = {
      v: 1,
      c: c,
      o: { ...options },
      t: { ...toggles }
    };

    if (embroideryConfig) {
      payload.e = { ...embroideryConfig };
    }

    if (canopies) {
      payload.cp = {};
      if (canopies.main && canopies.main.handle) {
        payload.cp.m = canopies.main.handle;
        payload.cp.mv = canopies.main.variantId;
      }
      if (canopies.reserve && canopies.reserve.handle) {
        payload.cp.r = canopies.reserve.handle;
        payload.cp.rv = canopies.reserve.variantId;
      }
    }

    return payload;
  }

  /**
   * Encode a save payload to a compressed config code string.
   */
  async function encodeConfigCode(payload) {
    const json = JSON.stringify(payload);
    if (canCompress()) {
      const compressed = await deflate(textEncoder.encode(json));
      return PREFIX_V2 + base64UrlEncodeBytes(compressed);
    }
    return PREFIX_V1 + base64UrlEncode(json);
  }

  /**
   * Decode a config code string back to a save payload.
   */
  async function decodeConfigCode(code) {
    if (code.startsWith(PREFIX_V2)) {
      const compressed = base64UrlDecodeToBytes(code.slice(PREFIX_V2.length));
      const raw = await inflate(compressed);
      return JSON.parse(textDecoder.decode(raw));
    }
    if (code.startsWith(PREFIX_V1)) {
      return JSON.parse(base64UrlDecode(code.slice(PREFIX_V1.length)));
    }
    throw new Error('Unsupported Infinity configuration code version');
  }

  /**
   * Build a full reopen link with the design code as a query parameter.
   */
  async function buildReopenLink(productUrl, payload) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('design', await encodeConfigCode(payload));
    return url.toString();
  }

  /**
   * Parse a reopen link and extract the design payload.
   */
  async function parseReopenLink(link) {
    const url = new URL(link);
    const code = url.searchParams.get('design');
    if (!code) return null;
    return decodeConfigCode(code);
  }

  /**
   * Shorten a URL via the Cloudflare Worker (graceful fallback).
   */
  async function shortenUrl(longUrl) {
    try {
      const res = await fetch(WORKER_ENDPOINT + '/shorten', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: longUrl, prefix: 'inf' })
      });
      if (!res.ok) return longUrl;
      const data = await res.json();
      return data.shortURL || longUrl;
    } catch {
      return longUrl;
    }
  }

  window.InfinityConfigCode = {
    buildSavePayload,
    encodeConfigCode,
    decodeConfigCode,
    buildReopenLink,
    parseReopenLink,
    shortenUrl,
    WORKER_ENDPOINT
  };
})();
